import { useState, useEffect, useMemo, useSyncExternalStore } from 'react';
import { Photo } from '../types';
import { PhotoCard } from './PhotoCard';

interface PhotoGalleryProps {
  photos: Photo[];
  onViewDetails: (photo: Photo) => void;
}

// Row patterns for desktop: 1–2 images per row with varied proportions
const rowPatterns: number[][] = [
  [1.5, 1],        // wider left
  [1],             // full width
  [1, 1.5],        // wider right
  [1, 1],          // equal pair
  [1],             // full width
  [1.3, 1],        // slightly wider left
  [1, 1.3],        // slightly wider right
  [1],             // full width
  [1, 1],          // equal pair
  [1.6, 1],        // much wider left
  [1],             // full width
  [1, 1.6],        // much wider right
];

const rowHeights = [
  '52vh', '60vh', '48vh', '45vh', '65vh', '50vh',
  '46vh', '58vh', '44vh', '50vh', '62vh', '48vh',
];

// Mobile: single column dominant with occasional pairs
const mobileRowPatterns: number[][] = [
  [1],
  [1, 1],
  [1],
  [1],
  [1.3, 1],
  [1],
  [1, 1.3],
  [1],
  [1],
  [1, 1],
  [1],
  [1],
];

const mobileRowHeights = [
  '38vh', '28vh', '42vh', '35vh', '30vh', '40vh',
  '28vh', '36vh', '44vh', '26vh', '38vh', '34vh',
];

// Seeded pseudo-random shuffle for consistent layout
function seededShuffle<T>(arr: T[], seed: number): T[] {
  const shuffled = [...arr];
  let s = seed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    const j = s % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Media query hook
const mobileQuery = typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)') : null;
function subscribeToMobile(cb: () => void) {
  mobileQuery?.addEventListener('change', cb);
  return () => mobileQuery?.removeEventListener('change', cb);
}
function getIsMobile() {
  return mobileQuery?.matches ?? false;
}

interface RowData {
  photos: Photo[];
  pattern: number[];
  height: string;
  startIndex: number;
}

function buildRows(photos: Photo[], isMobile: boolean): RowData[] {
  const rows: RowData[] = [];
  let photoIndex = 0;
  let patternIndex = 0;

  const patterns = isMobile ? mobileRowPatterns : rowPatterns;
  const heights = isMobile ? mobileRowHeights : rowHeights;

  // Shuffle patterns based on photo count for "random" but stable layout
  const shuffled = seededShuffle(
    patterns.map((p, i) => ({ pattern: p, height: heights[i % heights.length] })),
    photos.length * 13 + 47
  );

  while (photoIndex < photos.length) {
    const entry = shuffled[patternIndex % shuffled.length];
    const count = Math.min(entry.pattern.length, photos.length - photoIndex);
    const rowPhotos = photos.slice(photoIndex, photoIndex + count);
    const rowPattern = entry.pattern.slice(0, count);

    rows.push({ photos: rowPhotos, pattern: rowPattern, height: entry.height, startIndex: photoIndex });
    photoIndex += count;
    patternIndex++;
  }

  return rows;
}

export function PhotoGallery({ photos, onViewDetails }: PhotoGalleryProps) {
  const [rotationOffset, setRotationOffset] = useState(0);
  const isMobile = useSyncExternalStore(subscribeToMobile, getIsMobile, () => false);

  // Rotate photos every 30 minutes
  useEffect(() => {
    const now = new Date();
    const minutesSinceMidnight = now.getHours() * 60 + now.getMinutes();
    const rotationNumber = Math.floor(minutesSinceMidnight / 30);
    setRotationOffset(rotationNumber * 7);

    const interval = setInterval(() => {
      setRotationOffset(prev => prev + 7);
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Get 7 photos starting from current rotation offset
  const displayPhotos = useMemo(() => {
    if (photos.length === 0) return [];
    if (photos.length <= 7) return photos;

    const startIndex = rotationOffset % photos.length;
    const rotated = [];
    for (let i = 0; i < 7; i++) {
      const index = (startIndex + i) % photos.length;
      rotated.push(photos[index]);
    }
    return rotated;
  }, [photos, rotationOffset]);

  const rows = useMemo(() => buildRows(displayPhotos, isMobile), [displayPhotos, isMobile]);

  return (
    <div className="relative z-10 bg-black overflow-hidden m-0 p-0 -translate-y-px">
      <div className="flex flex-col gap-[2px]">
        {rows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex gap-[2px]">
            {row.photos.map((photo, i) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                index={row.startIndex + i}
                onViewDetails={onViewDetails}
                flexGrow={row.pattern[i]}
                height={row.height}
                indexInRow={i}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Bottom spacer */}
      <div className="h-16 md:h-24 bg-black" />
    </div>
  );
}

export default PhotoGallery;